import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VocabularyManagement from './VocabularyManagement';
import ExamManagement from './ExamManagement';
import ShopManagement from './ShopManagement';
import ESPCourses from './ESPCourses';
import CourseLibrary from './CourseLibrary';
import LiveLearning from './LiveLearning';
import LiveCommunication from './LiveCommunication';
import Reading from './Reading'; // New import
import WritingDictation from './WritingDictation'; // New import for Writing & Dictation
import GeneralVocabularyManagement from './GeneralVocabularyManagement'; // New import for General Vocabulary
import './CourseManagement.css';

const ADMIN_LANGUAGES = [
  { code: 'en', flagCountryCode: 'gb', label: 'English' },
  { code: 'fr', flagCountryCode: 'fr', label: 'French' },
  { code: 'es', flagCountryCode: 'es', label: 'Spanish' },
  { code: 'de', flagCountryCode: 'de', label: 'German' },
  { code: 'it', flagCountryCode: 'it', label: 'Italian' }
];
const FLAG_IMG_BASE = 'https://flagcdn.com/w80';

const CourseManagement = () => {
  const { t } = useTranslation('navigation');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAdminLanguage, setSelectedAdminLanguage] = useState(null); // null = only language cards; code = show features for that language
  const [showVocabularyManagement, setShowVocabularyManagement] = useState(false);
  const [vocabularyInitialTab, setVocabularyInitialTab] = useState('articles');
  const [showExamManagement, setShowExamManagement] = useState(false);
  const [examInitialSection, setExamInitialSection] = useState('overview');
  const [showShopManagement, setShowShopManagement] = useState(false);
  const [showESPCourses, setShowESPCourses] = useState(false);
  const [showCourseLibrary, setShowCourseLibrary] = useState(false);
  const [showLiveLearning, setShowLiveLearning] = useState(false);
  const [showLiveCommunication, setShowLiveCommunication] = useState(false);
  const [showReading, setShowReading] = useState(false); // New state
  const [showWritingDictation, setShowWritingDictation] = useState(false); // New state for Writing & Dictation
  const [showGeneralVocabularyManagement, setShowGeneralVocabularyManagement] = useState(false); // New state for General Vocabulary
  const [generalVocabularyInitialSection, setGeneralVocabularyInitialSection] = useState('overview');

  const selectedAdminLanguageLabel = (selectedAdminLanguage
    ? (ADMIN_LANGUAGES.find((l) => l.code === selectedAdminLanguage)?.label || 'English')
    : 'English'
  );
  const currentAdminLanguage = selectedAdminLanguage
    ? ADMIN_LANGUAGES.find((l) => l.code === selectedAdminLanguage) || null
    : null;

  const adminFeatures = [
    {
      title: t('courseManagement.features.vocabularyManagement.title'),
      icon: '🧠',
      description: t('courseManagement.features.vocabularyManagement.description'),
      action: 'vocabulary-management',
      category: t('courseManagement.categories.contentManagement')
    },
    {
      title: t('courseManagement.features.generalVocabulary.title'),
      icon: '📚',
      description: t('courseManagement.features.generalVocabulary.description'),
      action: 'general-vocabulary',
      category: t('courseManagement.categories.contentManagement')
    },
    {
      title: t('courseManagement.features.readingManagement.title'),
      icon: '📖',
      description: t('courseManagement.features.readingManagement.description'),
      action: 'reading-management',
      category: t('courseManagement.categories.contentManagement')
    },
    {
      title: t('courseManagement.features.writingDictation.title'),
      icon: '✍️',
      description: t('courseManagement.features.writingDictation.description'),
      action: 'writing-dictation-management',
      category: t('courseManagement.categories.contentManagement')
    },
    {
      title: t('courseManagement.features.examPreparation.title'),
      icon: '📝',
      description: t('courseManagement.features.examPreparation.description'),
      action: 'exam-preparation',
      category: t('courseManagement.categories.contentManagement')
    },
    {
      title: t('courseManagement.features.courseLibrary.title'),
      icon: '📚',
      description: t('courseManagement.features.courseLibrary.description'),
      action: 'course-library',
      category: t('courseManagement.categories.courseManagement')
    },
    {
      title: t('courseManagement.features.espCourses.title'),
      icon: '🧪',
      description: t('courseManagement.features.espCourses.description'),
      action: 'esp-courses',
      category: t('courseManagement.categories.courseManagement')
    },

    {
      title: t('courseManagement.features.liveLearning.title'),
      icon: '🎓',
      description: t('courseManagement.features.liveLearning.description'),
      action: 'live-learning',
      category: t('courseManagement.categories.livePlatform')
    },
    {
      title: t('courseManagement.features.liveCommunication.title'),
      icon: '💬',
      description: t('courseManagement.features.liveCommunication.description'),
      action: 'live-communication',
      category: t('courseManagement.categories.livePlatform')
    },
    {
      title: t('courseManagement.features.educationalStore.title'),
      icon: '🛒',
      description: t('courseManagement.features.educationalStore.description'),
      action: 'educational-store',
      category: t('courseManagement.categories.ecommerce')
    }
  ];

  const LANGUAGE_RELATED_ACTIONS = ['vocabulary-management', 'general-vocabulary', 'reading-management', 'writing-dictation-management', 'exam-preparation', 'esp-courses'];
  const nonLanguageFeatures = adminFeatures.filter(f => !LANGUAGE_RELATED_ACTIONS.includes(f.action));
  const languageFeatures = adminFeatures.filter(f => LANGUAGE_RELATED_ACTIONS.includes(f.action));
  const nonLanguageCategories = [...new Set(nonLanguageFeatures.map(f => f.category))];
  const languageCategories = [...new Set(languageFeatures.map(f => f.category))];

  const renderFeatureSections = (features, catList) => (
    <div className="feature-cards-container">
      {catList.map(category => (
        <div key={category} className="category-section">
          <h3 className="category-title">{category}</h3>
          <div className="feature-cards-grid course-cards-grid">
            {features
              .filter(feature => feature.category === category)
              .map((feature, index) => (
                <div key={feature.action} className="feature-card">
                  <div className="card-header">
                    <div className="icon">{feature.icon}</div>
                    <h3 className="card-title">{feature.title}</h3>
                  </div>
                  <p className="card-description">{feature.description}</p>
                  <div className="card-footer">
                    <button
                      type="button"
                      className="access-link"
                      onClick={() => handleCardClick(feature)}
                    >
                      {t('courseManagement.accessModule')}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );

  const handleCardClick = (feature) => {
    if (feature.action === 'vocabulary-management') {
      setVocabularyInitialTab('articles');
      setShowVocabularyManagement(true);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'vocabulary-management-jobs') {
      setVocabularyInitialTab('jobs');
      setShowVocabularyManagement(true);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false);
    } else if (feature.action === 'general-vocabulary') {
      setGeneralVocabularyInitialSection('overview');
      setShowGeneralVocabularyManagement(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
    } else if (feature.action === 'general-vocabulary-jobs') {
      setGeneralVocabularyInitialSection('jobs');
      setShowGeneralVocabularyManagement(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
    } else if (feature.action === 'course-library') {
      setShowCourseLibrary(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'live-learning') {
      setShowLiveLearning(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'live-communication') {
      setShowLiveCommunication(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'reading-management') {
      setShowReading(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowWritingDictation(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'writing-dictation-management') {
      setShowWritingDictation(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'esp-courses') {
      setShowESPCourses(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowShopManagement(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'exam-preparation') {
      setExamInitialSection('overview');
      setShowExamManagement(true);
      setShowVocabularyManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    } else if (feature.action === 'exam-preparation-jobs') {
      setExamInitialSection('jobs');
      setShowExamManagement(true);
      setShowVocabularyManagement(false);
      setShowShopManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false);
    } else if (feature.action === 'educational-store') {
      setShowShopManagement(true);
      setShowVocabularyManagement(false);
      setShowExamManagement(false);
      setShowESPCourses(false);
      setShowCourseLibrary(false);
      setShowLiveLearning(false);
      setShowLiveCommunication(false);
      setShowReading(false);
      setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    }
  };

  const handleBackToCourseManagement = () => {
    setShowVocabularyManagement(false);
    setShowExamManagement(false);
    setShowShopManagement(false);
    setShowESPCourses(false);
    setShowCourseLibrary(false);
    setShowLiveLearning(false);
    setShowLiveCommunication(false);
    setShowReading(false);
    setShowWritingDictation(false);
    setShowGeneralVocabularyManagement(false); // Hide General Vocabulary
    setVocabularyInitialTab('articles');
    setGeneralVocabularyInitialSection('overview');
    setExamInitialSection('overview');
  };

  if (showVocabularyManagement) {
    return (
      <div className="vocabulary-management-wrapper">
        <button 
          className="back-to-course-management" 
          onClick={handleBackToCourseManagement}
        >
          <span className="material-icons">arrow_back</span>
          {t('courseManagement.backToCourseManagement')}
        </button>
        <VocabularyManagement initialInnerTab={vocabularyInitialTab} selectedLanguage={selectedAdminLanguageLabel} />
      </div>
    );
  }

  if (showExamManagement) {
    return (
      <div className="exam-management-wrapper">
        <button 
          className="back-to-course-management" 
          onClick={handleBackToCourseManagement}
        >
          <span className="material-icons">arrow_back</span>
          {t('courseManagement.backToCourseManagement')}
        </button>
        <ExamManagement initialSection={examInitialSection} selectedLanguage={selectedAdminLanguageLabel} />
      </div>
    );
  }

  if (showShopManagement) {
    return (
      <div className="shop-management-wrapper">
        <button 
          className="back-to-course-management" 
          onClick={handleBackToCourseManagement}
        >
          <span className="material-icons">arrow_back</span>
          {t('courseManagement.backToCourseManagement')}
        </button>
        <ShopManagement />
      </div>
    );
  }

  if (showESPCourses) {
    return (
      <div className="esp-courses-wrapper">
        <ESPCourses onBack={handleBackToCourseManagement} selectedLanguage={selectedAdminLanguageLabel} />
      </div>
    );
  }

  if (showCourseLibrary) {
    return (
      <div className="course-library-wrapper">
        <CourseLibrary onBack={handleBackToCourseManagement} />
      </div>
    );
  }

  if (showLiveLearning) {
    return (
      <div className="live-learning-wrapper">
        <LiveLearning onBack={handleBackToCourseManagement} />
      </div>
    );
  }

  if (showLiveCommunication) {
    return (
      <div className="live-communication-wrapper">
        <LiveCommunication onBack={handleBackToCourseManagement} />
      </div>
    );
  }

  if (showReading) {
    return (
      <div className="reading-wrapper">
        <Reading onBack={handleBackToCourseManagement} selectedLanguage={selectedAdminLanguageLabel} />
      </div>
    );
  }

  if (showWritingDictation) {
    return (
      <div className="writing-dictation-management-wrapper">
        <WritingDictation onBack={handleBackToCourseManagement} selectedLanguage={selectedAdminLanguageLabel} />
      </div>
    );
  }

  if (showGeneralVocabularyManagement) {
    return (
      <div className="general-vocabulary-management-wrapper">
        <button 
          className="back-to-course-management" 
          onClick={handleBackToCourseManagement}
        >
          <span className="material-icons">arrow_back</span>
          {t('courseManagement.backToCourseManagement')}
        </button>
        <GeneralVocabularyManagement onBack={handleBackToCourseManagement} initialSection={generalVocabularyInitialSection} selectedLanguage={selectedAdminLanguageLabel} />
      </div>
    );
  }

  return (
    <div className="section course-management-section">
      <div className="course-management-header-card">
        <h2 className="section-title">{t('courseManagement.title')}</h2>
        <p className="section-subtitle">{t('courseManagement.subtitle')}</p>
      </div>

      {selectedAdminLanguage == null ? (
        <>
          <div className="course-management-language-card">
            <p className="course-management-language-title">Choose a language</p>
            <div className="course-management-language-grid">
              {ADMIN_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className="course-management-language-card-item"
                  onClick={() => setSelectedAdminLanguage(lang.code)}
                >
                  <img
                    src={`${FLAG_IMG_BASE}/${lang.flagCountryCode || lang.code}.png`}
                    alt=""
                    className="course-management-flag-img"
                  />
                  <span className="course-management-language-label">{lang.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="course-management-features-card">
            {renderFeatureSections(nonLanguageFeatures, nonLanguageCategories)}
          </div>
        </>
      ) : (
        <div className="course-management-features-card">
          <div className="course-management-back-row">
            <button
              type="button"
              className="course-management-change-language"
              onClick={() => setSelectedAdminLanguage(null)}
            >
              <span className="material-icons">arrow_back</span>
              Change language
              {currentAdminLanguage && (
                <>
                  <span style={{ margin: '0 4px' }}>·</span>
                  <img
                    src={`${FLAG_IMG_BASE}/${currentAdminLanguage.flagCountryCode || currentAdminLanguage.code}.png`}
                    alt={currentAdminLanguage.label}
                    className="course-management-flag-img"
                    style={{ width: 20, height: 14, objectFit: 'cover' }}
                  />
                  <span className="course-management-language-label" style={{ marginLeft: 4 }}>
                    {currentAdminLanguage.label}
                  </span>
                </>
              )}
            </button>
          </div>
          {renderFeatureSections(languageFeatures, languageCategories)}
        </div>
      )}
    </div>
  );
};

export default CourseManagement; 